/**
 * Transportation Segments Utility
 * Extracted from TransportationInfoTab to be reusable
 */

import { safeFormatDate, buildGoogleMapsQuery } from './utils';
import { isDayWithinAccommodationRange } from './activityUtils';

// Helper to extract date part from ISO string (YYYY-MM-DD)
function extractDatePart(dateString) {
  if (!dateString || typeof dateString !== 'string') return '';
  try {
    const datePart = dateString.split('T')[0];
    return datePart || '';
  } catch {
    return '';
  }
}

// Helper to get address from activity location
function getAddressFromLocation(activity, role) {
  // role: 'from' | 'to' for transportation; otherwise use location
  try {
    if (activity.type === 'transportation') {
      const loc = role === 'from' ? activity.fromLocation : activity.toLocation;
      if (!loc) return '';
      if (typeof loc === 'string') return loc;
      if (typeof loc === 'object') return loc.address || '';
      return '';
    }
    const loc = activity.location;
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    if (typeof loc === 'object') {
      const address = loc.address || '';
      if (address) return address;
      // Fallback to query builder when address is missing.
      return buildGoogleMapsQuery(activity, 'any') || '';
    }
    return '';
  } catch {
    return '';
  }
}

// Find accommodation for a specific day (that spans this day)
function findAccommodationForDay(dayDate, allDays) {
  if (!allDays || !Array.isArray(allDays)) return null;
  
  for (const day of allDays) {
    if (day.activities && Array.isArray(day.activities)) {
      for (const activity of day.activities) {
        if (activity.type === 'accommodation' && 
            activity.startDateTime && 
            activity.endDateTime &&
            isDayWithinAccommodationRange(dayDate, activity.startDateTime, activity.endDateTime)) {
          return activity;
        }
      }
    }
  }
  return null;
}

// Find accommodation that ENDS on a specific day (from previous days)
function findAccommodationEndingOnDay(dayDate, allDays) {
  if (!allDays || !Array.isArray(allDays)) return null;
  
  const dayDateStr = extractDatePart(dayDate);
  if (!dayDateStr) return null;
  
  for (const day of allDays) {
    if (day.activities && Array.isArray(day.activities)) {
      for (const activity of day.activities) {
        if (activity.type === 'accommodation' && activity.endDateTime) {
          const endDateStr = extractDatePart(activity.endDateTime);
          if (endDateStr === dayDateStr) {
            return activity;
          }
        }
      }
    }
  }
  return null;
}

/**
 * Build transportation segments from plan data
 * Returns both segments array (for display) and a Map of activityId -> nextActivityInfo (for lookup)
 * @param {Object} parsedData - Plan data with days array
 * @returns {Object} { segments: Array, nextActivityMap: Map }
 */
export function buildTransportationSegments(parsedData) {
  if (!parsedData || !Array.isArray(parsedData.days)) {
    return { segments: [], nextActivityMap: new Map(), previousActivityMap: new Map() };
  }

  const allSegments = [];
  const nextActivityMap = new Map(); // activityId -> { activity, role }
  const previousActivityMap = new Map(); // activityId -> { activity, role }

  // Helper to push a segment and update the lookup map
  const pushSegment = (fromActivity, toActivity, fromAddress, toAddress, dayDateLabel, dayNumber) => {
    if (!fromActivity || !toActivity || !fromAddress || !toAddress) return;
    
    // Check for duplicates: same day, same from/to IDs
    const isDuplicate = allSegments.some(seg => 
      seg.dayDate === dayDateLabel &&
      seg.fromId === fromActivity.id &&
      seg.toId === toActivity.id
    );
    
    if (isDuplicate) return; // Skip duplicate
    
    // Determine the role for "from" activity
    let fromRole = 'any';
    if (fromActivity.type === 'transportation') {
      fromRole = 'to'; // When transportation is the "from" activity, we're at its destination
    }
    
    // Determine the role for "to" activity
    let toRole = 'any';
    if (toActivity.type === 'transportation') {
      toRole = 'from'; // When transportation is the "to" activity, we're going to its origin
    }
    
    // Add to segments array
    allSegments.push({
      dayDate: dayDateLabel,
      dayNumber: dayNumber != null ? dayNumber : dayDateLabel,
      fromId: fromActivity.id,
      toId: toActivity.id,
      fromName: fromActivity.name || 'Untitled',
      fromType: fromActivity.type || 'activity',
      fromTime: safeFormatDate(fromActivity.startDateTime, 'time') || '',
      fromAddress: fromAddress,
      fromActivity: fromActivity,
      fromRole: fromRole,
      toName: toActivity.name || 'Untitled',
      toType: toActivity.type || 'activity',
      toTime: safeFormatDate(toActivity.startDateTime, 'time') || '',
      toAddress: toAddress,
      toActivity: toActivity,
      toRole: toRole
    });

    // Keep the first deterministic segment per activity to avoid unstable overrides.
    if (!nextActivityMap.has(fromActivity.id)) {
      nextActivityMap.set(fromActivity.id, {
        activity: toActivity,
        role: toRole
      });
    }
    if (!previousActivityMap.has(toActivity.id)) {
      previousActivityMap.set(toActivity.id, {
        activity: fromActivity,
        role: fromRole
      });
    }
  };

  // Iterate days in order
  for (let i = 0; i < parsedData.days.length; i++) {
    const day = parsedData.days[i];
    const dayDate = safeFormatDate(day?.date, 'yyyy-MM-dd');
    if (!day || !Array.isArray(day.activities) || day.activities.length === 0) {
      continue;
    }

    // Find accommodation for this day
    const dayAccommodation = findAccommodationForDay(day.date, parsedData.days);
    
    // Include ALL activities in sequence (including accommodations)
    // Sort activities by start time
    const activitiesSorted = [...day.activities].sort((a, b) => {
      const ta = a.startDateTime ? (a.startDateTime.split('T')[1] || '') : '';
      const tb = b.startDateTime ? (b.startDateTime.split('T')[1] || '') : '';
      return ta.localeCompare(tb);
    });

    if (activitiesSorted.length === 0) {
      continue;
    }

    // Check if an accommodation ENDS on this day (from a previous day)
    const accommodationEndingToday = findAccommodationEndingOnDay(day.date, parsedData.days);
    
    // Day starts from accommodation (except first day)
    const firstActivity = activitiesSorted[0];
    const accommodationInSequence = dayAccommodation && activitiesSorted.some(a => {
      if (a.type !== 'accommodation') return false;
      if (a.id === dayAccommodation.id) return true;
      if (a.name === dayAccommodation.name) {
        const aAddr = typeof a.location === 'string' ? a.location : (a.location?.address || '');
        const dayAddr = typeof dayAccommodation.location === 'string' ? dayAccommodation.location : (dayAccommodation.location?.address || '');
        if (aAddr && dayAddr && aAddr === dayAddr) return true;
      }
      return false;
    });
    
    const firstActivityIsAccommodation = firstActivity.type === 'accommodation';
    
    // If an accommodation ends today and first activity is also an accommodation, create segment between them
    if (accommodationEndingToday && firstActivityIsAccommodation) {
      const fromAddress = getAddressFromLocation(accommodationEndingToday, 'any');
      const toAddress = getAddressFromLocation(firstActivity, 'any');
      
      if (fromAddress && toAddress) {
        pushSegment(accommodationEndingToday, firstActivity, fromAddress, toAddress, dayDate, i + 1);
      }
    }
    // Otherwise, if day starts from spanning accommodation (not in sequence)
    else if (dayAccommodation && !firstActivityIsAccommodation && !accommodationInSequence) {
      const fromAddress = getAddressFromLocation(dayAccommodation, 'any');
      let toAddress;
      
      if (firstActivity.type === 'transportation') {
        toAddress = getAddressFromLocation(firstActivity, 'from');
      } else {
        toAddress = getAddressFromLocation(firstActivity, 'any');
      }
      
      if (fromAddress && toAddress) {
        pushSegment(dayAccommodation, firstActivity, fromAddress, toAddress, dayDate, i + 1);
      }
    }

    // Within-day segments between ALL consecutive activities (including accommodations)
    for (let j = 0; j < activitiesSorted.length - 1; j++) {
      const fromActivity = activitiesSorted[j];
      const toActivity = activitiesSorted[j + 1];
      
      let fromAddress;
      if (fromActivity.type === 'transportation') {
        fromAddress = getAddressFromLocation(fromActivity, 'to');
      } else {
        fromAddress = getAddressFromLocation(fromActivity, 'any');
      }
      
      let toAddress;
      if (toActivity.type === 'transportation') {
        toAddress = getAddressFromLocation(toActivity, 'from');
      } else {
        toAddress = getAddressFromLocation(toActivity, 'any');
      }
      
      if (fromAddress && toAddress) {
        pushSegment(fromActivity, toActivity, fromAddress, toAddress, dayDate, i + 1);
      }
    }

    // Day ends at accommodation (except last day)
    const lastActivity = activitiesSorted[activitiesSorted.length - 1];
    const accommodationInCurrentDay = activitiesSorted.find(a => a.type === 'accommodation');
    const accommodationForDayEnd = accommodationInCurrentDay || (dayAccommodation && !accommodationInSequence ? dayAccommodation : null);
    
    if (i < parsedData.days.length - 1 && 
        accommodationForDayEnd && 
        lastActivity.type !== 'accommodation' && 
        lastActivity.type !== 'transportation' &&
        lastActivity.id !== accommodationForDayEnd.id) {
      const fromAddress = getAddressFromLocation(lastActivity, 'any');
      const toAddress = getAddressFromLocation(accommodationForDayEnd, 'any');
      
      if (fromAddress && toAddress) {
        pushSegment(lastActivity, accommodationForDayEnd, fromAddress, toAddress, dayDate, i + 1);
      }
    }

    // Cross-day segment: last activity of this day -> first activity of next day
    if (i < parsedData.days.length - 1) {
      const nextDay = parsedData.days[i + 1];
      
      if (nextDay && Array.isArray(nextDay.activities) && nextDay.activities.length > 0) {
        const nextDayActivities = [...nextDay.activities].sort((a, b) => {
          const ta = a.startDateTime ? (a.startDateTime.split('T')[1] || '') : '';
          const tb = b.startDateTime ? (b.startDateTime.split('T')[1] || '') : '';
          return ta.localeCompare(tb);
        });
        
        if (activitiesSorted.length > 0 && nextDayActivities.length > 0) {
          const lastOfCur = activitiesSorted[activitiesSorted.length - 1];
          const firstOfNext = nextDayActivities[0];
          
          const createdDayEndsAtAccommodation = (i < parsedData.days.length - 1 && dayAccommodation && lastActivity.type !== 'accommodation' && !accommodationInSequence);
          const nextDayAccommodation = findAccommodationForDay(nextDay.date, parsedData.days);
          const nextDayAccommodationInSequence = nextDayAccommodation && nextDayActivities.some(a => {
            if (a.type !== 'accommodation') return false;
            if (a.id === nextDayAccommodation.id) return true;
            if (a.name === nextDayAccommodation.name) {
              const aAddr = typeof a.location === 'string' ? a.location : (a.location?.address || '');
              const dayAddr = typeof nextDayAccommodation.location === 'string' ? nextDayAccommodation.location : (nextDayAccommodation.location?.address || '');
              if (aAddr && dayAddr && aAddr === dayAddr) return true;
            }
            return false;
          });
          const willCreateDayStartsFromAccommodation = (i > 0 && nextDayAccommodation && firstOfNext.type !== 'accommodation' && !nextDayAccommodationInSequence);
          
          if (!(createdDayEndsAtAccommodation && willCreateDayStartsFromAccommodation)) {
            let fromAddress;
            if (lastOfCur.type === 'transportation') {
              fromAddress = getAddressFromLocation(lastOfCur, 'to');
            } else {
              fromAddress = getAddressFromLocation(lastOfCur, 'any');
            }
            
            let toAddress;
            if (firstOfNext.type === 'transportation') {
              toAddress = getAddressFromLocation(firstOfNext, 'from');
            } else {
              toAddress = getAddressFromLocation(firstOfNext, 'any');
            }
            
            if (fromAddress && toAddress) {
              const nextDayDate = safeFormatDate(nextDay.date, 'yyyy-MM-dd');
              pushSegment(lastOfCur, firstOfNext, fromAddress, toAddress, nextDayDate, i + 2);
            }
          }
        }
      }
    }
  }

  return { segments: allSegments, nextActivityMap, previousActivityMap };
}
